import { MODALS } from 'components/ModalsConfig';
import { produce } from 'immer';
import { random } from 'lodash';
import * as actions from 'store/app/actions';
import { AppState } from 'store/app/types';
import { RESET_DEV_DATA, SIGN_OUT } from 'store/auth/actions';
import { DATA_SYNC } from 'store/sync/actions';
import { createReducer } from 'typesafe-actions';

const initialState: AppState = {
  modals: {},
  modalsOrder: [],
  modalsDiscarded: undefined,
  alert: undefined,
  blockingLoaderCount: 0,
  blockingLoaderKeys: [],
  blockingLoaderText: [],
  blockingLoaderWaitKeys: [],
  blockingLoaderCancelledKeys: [],
  generic: {},
  genericTemp: {},
  incomingValue: {},
  instance: `${Date.now()}.${random(100, 999)}`,
  instances: [],
  lastOfflineTime: undefined,
  lastOnlineTime: undefined,
  navigation: undefined,
  showSyncSummary: false,
  toastText: undefined,
  settingSavePhotosToMediaLibrary: null,
  isOffline: false,
};

export const appReducer = createReducer(initialState)
  .handleAction(SIGN_OUT.success, (state) => ({
    ...initialState,
    instance: state.instance,
    instances: state.instances,
  }))
  .handleAction(actions.doAppMerge, (state, action) => ({
    ...state,
    ...action.payload,
  }))
  .handleAction(actions.NAVIGATE_MODAL, (state, action) =>
    produce(state, (draft) => {
      // Only one modal of the same type is allowed
      if (state.modals[action.payload.screen]) {
        return;
      }
      const modal = MODALS[action.payload.screen];
      if (!modal) {
        throw new Error(`Modal '${action.payload.screen}' doesn't exist`);
      }
      draft.modalsOrder.push({
        key: action.payload.screen,
      });
      draft.modals[action.payload.screen] = {
        /** @ts-expect-error This should be valid. There may be an issue with the type given to this action */
        params: action.payload.params,
        size: action.payload.size || MODALS[action.payload.screen].size,
      };
    }),
  )
  .handleAction(actions.NAVIGATE_MODAL_BACK, (state, action) => {
    if (!state.modalsOrder.length) {
      return {
        ...state,
        modalsDiscarded: undefined,
      };
    }
    const lastModalKey = state.modalsOrder.length - 1;
    const lastModal = state.modalsOrder[lastModalKey]!;
    if (
      action.payload?.length &&
      !action.payload.find((screen) => screen === lastModal.key)
    ) {
      return {
        ...state,
        modalsDiscarded: undefined,
      };
    }
    return {
      ...state,
      modalsOrder: state.modalsOrder.filter((m) => m.key !== lastModal.key),
      modalsDiscarded: lastModal,
      modals: {
        ...state.modals,
        [lastModal.key]: undefined,
      },
    };
  })
  .handleAction(actions.SET_APP_INSTANCE, (state, action) => ({
    ...state,
    instances: [...state.instances, action.payload].splice(-250),
  }))
  .handleAction(DATA_SYNC.success, (state) => ({
    ...state,
    instances: [],
  }))
  .handleAction(actions.SET_LAST_ONLINE_TIME, (state, action) => ({
    ...state,
    lastOnlineTime: action.payload,
    isOffline: false,
  }))
  .handleAction(actions.SET_LAST_OFFLINE_TIME, (state, action) => ({
    ...state,
    lastOfflineTime: action.payload,
    isOffline: true,
  }))
  .handleAction(actions.SET_NAVIGATION_STATE, (state, action) => ({
    ...state,
    navigation: action.payload,
  }))
  .handleAction(actions.doShowLoading, (state, action) => ({
    ...state,
    blockingLoaderKeys: [
      ...state.blockingLoaderKeys,
      action.payload?.key || '-1',
    ],
    blockingLoaderCount: state.blockingLoaderCount + 1,
    blockingLoaderText: [
      ...state.blockingLoaderText,
      action.payload?.text || '',
    ],
    blockingLoaderWaitKeys: action.payload?.waitKey
      ? [...state.blockingLoaderWaitKeys, action.payload?.waitKey || '']
      : state.blockingLoaderWaitKeys,
  }))
  .handleAction(actions.SHOW_BLOCKING_LOADER, (state, action) => ({
    ...state,
    blockingLoaderKeys: [...state.blockingLoaderKeys, action.payload || '-1'],
    blockingLoaderText: [...state.blockingLoaderText, ''],
    blockingLoaderWaitKeys: [...state.blockingLoaderWaitKeys, ''],
    blockingLoaderCount: state.blockingLoaderCount + 1,
  }))
  .handleAction(actions.HIDE_BLOCKING_LOADER, (state, action) =>
    produce(state, (draft) => {
      const key = action.payload || '-1';
      const kIndex = state.blockingLoaderKeys.indexOf(key);
      if (kIndex > -1) {
        draft.blockingLoaderKeys.splice(kIndex, 1);
        draft.blockingLoaderWaitKeys.pop();
        draft.blockingLoaderCount = draft.blockingLoaderKeys.length;
      }
    }),
  )
  .handleAction(actions.doLoaderCancel, (state, action) =>
    produce(state, (draft) => {
      draft.blockingLoaderCancelledKeys.push(action.payload.key);
    }),
  )
  .handleAction(actions.ALERT, (state, action) => ({
    ...state,
    alert: action.payload,
  }))
  .handleAction(actions.CLEAR_ALERT, (state) => ({
    ...state,
    alert: undefined,
  }))
  .handleAction(actions.doShowSignatureDialog, (state, action) => ({
    ...state,
    signature: action.payload,
  }))
  .handleAction(actions.doHideSignatureDialog, (state) => ({
    ...state,
    signature: undefined,
  }))

  .handleAction(actions.CONFIRM, (state, action) => ({
    ...state,
    confirm: {
      ...action.payload,
      cancelText: action.payload.cancelText || 'Cancel',
      confirmText: action.payload.confirmText || 'Okay',
      dismissable: action.payload.dismissable !== false,
    },
  }))
  .handleAction(actions.CLEAR_CONFIRM, (state) => ({
    ...state,
    confirm: undefined,
  }))
  .handleAction(actions.SET_GENERIC, (state, action) =>
    produce(state, (draft) => {
      if (action.payload.value === undefined) {
        delete draft.generic[action.payload.key];
        delete draft.genericTemp[action.payload.key];
      } else if (action.payload.temp) {
        draft.genericTemp[action.payload.key] = action.payload.value;
        delete draft.generic[action.payload.key];
      } else {
        draft.generic[action.payload.key] = action.payload.value;
        delete draft.genericTemp[action.payload.key];
      }
    }),
  )
  .handleAction(actions.SET_GENERICS, (state, action) =>
    produce(state, (draft) => {
      action.payload.forEach((payload) => {
        if (payload.value === undefined) {
          delete draft.generic[payload.key];
          delete draft.genericTemp[payload.key];
        } else if (payload.temp) {
          draft.genericTemp[payload.key] = payload.value;
          delete draft.generic[payload.key];
        } else {
          draft.generic[payload.key] = payload.value;
          delete draft.genericTemp[payload.key];
        }
      });
    }),
  )
  .handleAction(actions.SET_INCOMING_VALUE, (state, action) =>
    produce(state, (draft) => {
      if (action.payload.value === undefined) {
        delete draft.incomingValue[action.payload.key];
      } else {
        draft.incomingValue[action.payload.key] = action.payload.value;
      }
    }),
  )
  .handleAction(actions.doToast, (state, action) => ({
    ...state,
    toastText: { timestamp: Date.now(), text: action.payload },
  }))
  .handleAction(actions.doToastError, (state, action) => ({
    ...state,
    toastText: {
      timestamp: Date.now(),
      text: action.payload,
      isError: true,
    },
  }))
  .handleAction(actions.doRemoveToast, (state) => ({
    ...state,
    toastText: undefined,
  }))
  .handleAction(actions.OPEN_BARCODE_SCANNER, (state, action) => ({
    ...state,
    barCodeScanner: { ...action.payload, instance: Date.now() },
  }))
  .handleAction(actions.UNLOAD_BARCODE_SCANNER, (state) => ({
    ...state,
    barCodeScanner: undefined,
  }))

  .handleAction(actions.doSetImagePreview, (state, action) => ({
    ...state,
    imagePreview: action.payload,
  }))
  .handleAction(actions.doUnsetImagePreview, (state) => ({
    ...state,
    imagePreview: undefined,
  }))

  .handleAction(RESET_DEV_DATA, (state) => ({
    ...state,
    navigation: undefined,
  }));
